import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../supabaseClient';
import './LocalidadesTab.css';
import { BotaoDownloadLocalidadePDF, FormularioLocalidadePDFDocument } from './FormularioLocalidadePDF';
import { pdf } from '@react-pdf/renderer';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

/* ══════════════════════════════════════════
   CONSTANTS
══════════════════════════════════════════ */
const RG03_DEFAULTS = {
    tipo_registro: 'inclusao',
    codigo_uf: '',
    codigo_municipio_ibge: '',
    data_rg: '',
    data_atualizacao: '',
    status_localidade: '1',
    qtd_imoveis_residenciais: '',
    qtd_imoveis_comerciais: '',
    qtd_outros_imoveis: '',
    qtd_habitantes: '',
    qtd_quarteiroes: '',
    qtd_pontos_estrategicos: '',
    qtd_armadilhas_instaladas: '',
    qtd_terrenos_baldios: '',
    infra_energia_eletrica: '',
    infra_agua_encanada: '',
    infra_tratamento_esgoto: '',
    infra_lavanderia_coletiva: '',
    infra_casas_privada: '',
    infra_coleta_lixo: '',
    infra_rede_telefonica: '',
    infra_transporte_publico: '',
    infra_rua_pavimentada: '',
    infra_escola: '',
    infra_posto_saude: '',
    infra_acesso_permanente: '',
    infra_pacs_psf: '',
    ocor_animais_cachorros: '',
    ocor_animais_gatos: '',
    ocor_animais_roedores: '',
    ocor_malaria: '',
    ocor_dengue: '',
    ocor_esquistossomose: '',
    ocor_leishmaniose: '',
    ocor_febre_maculosa: '',
    ocor_peste: '',
    ocor_doenca_chagas: '',
    ocor_febre_amarela: '',
    longitude: '',
    latitude: '',
    altitude_m: '',
    distancia_centro_km: '',
    visto_supervisor: '',
    data_visto: '',
    classificacao: '',
};

const RG03_CAMPOS_UPDATE = [
    'nome', 'codigo', 'categoria', 'classificacao',
    'tipo_registro', 'codigo_uf', 'codigo_municipio_ibge',
    'data_rg', 'data_atualizacao', 'status_localidade',
    'qtd_imoveis_residenciais', 'qtd_imoveis_comerciais', 'qtd_outros_imoveis',
    'qtd_habitantes', 'qtd_quarteiroes', 'qtd_pontos_estrategicos',
    'qtd_armadilhas_instaladas', 'qtd_terrenos_baldios',
    'infra_energia_eletrica', 'infra_agua_encanada', 'infra_tratamento_esgoto',
    'infra_lavanderia_coletiva', 'infra_casas_privada', 'infra_coleta_lixo',
    'infra_rede_telefonica', 'infra_transporte_publico', 'infra_rua_pavimentada',
    'infra_escola', 'infra_posto_saude', 'infra_acesso_permanente', 'infra_pacs_psf',
    'ocor_animais_cachorros', 'ocor_animais_gatos', 'ocor_animais_roedores',
    'ocor_malaria', 'ocor_dengue', 'ocor_esquistossomose', 'ocor_leishmaniose',
    'ocor_febre_maculosa', 'ocor_peste', 'ocor_doenca_chagas', 'ocor_febre_amarela',
    'longitude', 'latitude', 'altitude_m', 'distancia_centro_km',
    'visto_supervisor', 'data_visto',
];

const FORM_VAZIO = { ...RG03_DEFAULTS, nome: '', codigo: '', categoria: '' };

/* ══════════════════════════════════════════
   HELPERS
══════════════════════════════════════════ */
const buildPayload = (form) => {
    const payload = {};
    RG03_CAMPOS_UPDATE.forEach(f => {
        payload[f] = form[f] === '' ? null : form[f];
    });
    payload.codigo = payload.codigo || null;
    return payload;
};

/* ══════════════════════════════════════════
   SUB-COMPONENTS
══════════════════════════════════════════ */

const SNIInput = React.memo(({ field, label, value, onChange }) => (
    <label className="loc-sni-row" style={{ cursor: 'pointer' }}>
        <span className="loc-sni-label">{label}</span>
        <input
            type="text"
            maxLength={1}
            className="loc-sni-input"
            value={value || ''}
            onChange={e => onChange(field, e.target.value.toUpperCase())}
            placeholder=" "
        />
    </label>
));

const SectionDivider = React.memo(({ label }) => (
    <div className="loc-section-divider">
        <span>{label}</span>
    </div>
));

const FormFields = React.memo(({ form, set, formPrefix }) => {
    const setNum = (field, value) => set(field, value === '' ? '' : Number(value));

    return (
        <div>
            <div className="loc-type-row">
                <label className={`loc-type-cell${form.tipo_registro === 'inclusao' ? ' selected' : ''}`}>
                    <input
                        type="radio"
                        name={`${formPrefix}_tipo`}
                        value="inclusao"
                        checked={form.tipo_registro === 'inclusao'}
                        onChange={e => set('tipo_registro', e.target.value)}
                        className="loc-radio-native"
                    />
                    INCLUSÃO
                </label>
                <label className={`loc-type-cell${form.tipo_registro === 'alteracao' ? ' selected' : ''}`}>
                    <input
                        type="radio"
                        name={`${formPrefix}_tipo`}
                        value="alteracao"
                        checked={form.tipo_registro === 'alteracao'}
                        onChange={e => set('tipo_registro', e.target.value)}
                        className="loc-radio-native"
                    />
                    ALTERAÇÃO
                </label>
            </div>

            <div className="loc-field-grid-2">
                <label className="loc-field">
                    <span className="loc-label">Código e nome UF (IBGE)</span>
                    <input
                        className="loc-input"
                        value={form.codigo_uf || ''}
                        onChange={e => set('codigo_uf', e.target.value)}
                        placeholder="Ex: 35 - São Paulo"
                    />
                </label>
                <div className="loc-field">
                    <span className="loc-label">Código e nome do município (IBGE)</span>
                    <input
                        className="loc-input loc-input-readonly"
                        value={form.codigo_municipio_ibge || ''}
                        readOnly
                    />
                </div>
            </div>

            <div className="loc-field-grid-2">
                <label className="loc-field">
                    <span className="loc-label">Cód. Localidade</span>
                    <input
                        className="loc-input"
                        value={form.codigo || ''}
                        onChange={e => set('codigo', e.target.value)}
                        placeholder="Código"
                    />
                </label>
                <label className="loc-field">
                    <span className="loc-label">Nome da Localidade *</span>
                    <input
                        className="loc-input"
                        value={form.nome || ''}
                        onChange={e => set('nome', e.target.value)}
                        required
                        placeholder="Nome da localidade"
                    />
                </label>
            </div>

            <div className="loc-field-grid-1">
                <label className="loc-field">
                    <span className="loc-label">Categoria da Localidade</span>
                    <input
                        className="loc-input"
                        value={form.categoria || ''}
                        onChange={e => set('categoria', e.target.value)}
                        placeholder="Ex: Vila, Bairro, Distrito..."
                    />
                </label>
            </div>

            <div className="loc-field-grid-2">
                <label className="loc-field">
                    <span className="loc-label">Data de Reconhecimento</span>
                    <input
                        type="date"
                        className="loc-input-date"
                        value={form.data_rg || ''}
                        onChange={e => set('data_rg', e.target.value)}
                    />
                </label>
                <label className="loc-field">
                    <span className="loc-label">Data de Atualização</span>
                    <input
                        type="date"
                        className="loc-input-date"
                        value={form.data_atualizacao || ''}
                        onChange={e => set('data_atualizacao', e.target.value)}
                    />
                </label>
            </div>

            <div className="loc-field-grid-2">
                <div className="loc-field">
                    <span className="loc-label">Status da Localidade</span>
                    <div className="loc-radio-group">
                        <label className="loc-radio-label">
                            <input
                                type="radio"
                                name={`${formPrefix}_status`}
                                value="1"
                                checked={form.status_localidade === '1'}
                                onChange={e => set('status_localidade', e.target.value)}
                            />
                            1-Ativa
                        </label>
                        <label className="loc-radio-label">
                            <input
                                type="radio"
                                name={`${formPrefix}_status`}
                                value="2"
                                checked={form.status_localidade === '2'}
                                onChange={e => set('status_localidade', e.target.value)}
                            />
                            2-Extinta
                        </label>
                    </div>
                </div>
                <div className="loc-field">
                    <span className="loc-label">Classificação da Localidade</span>
                    <div className="loc-radio-group">
                        <label className="loc-radio-label">
                            <input
                                type="radio"
                                name={`${formPrefix}_classif`}
                                value="Urbana"
                                checked={form.classificacao === 'Urbana'}
                                onChange={e => set('classificacao', e.target.value)}
                            />
                            U-Urbana
                        </label>
                        <label className="loc-radio-label">
                            <input
                                type="radio"
                                name={`${formPrefix}_classif`}
                                value="Rural"
                                checked={form.classificacao === 'Rural'}
                                onChange={e => set('classificacao', e.target.value)}
                            />
                            R-Rural
                        </label>
                    </div>
                </div>
            </div>

            <SectionDivider label="Quantidades" />
            <div className="loc-field-grid-2">
                <label className="loc-field">
                    <span className="loc-label">Imóveis Residenciais</span>
                    <input
                        type="number"
                        min="0"
                        className="loc-input-num"
                        value={form.qtd_imoveis_residenciais ?? ''}
                        onChange={e => setNum('qtd_imoveis_residenciais', e.target.value)}
                    />
                </label>
                <label className="loc-field">
                    <span className="loc-label">Imóveis Comerciais</span>
                    <input
                        type="number"
                        min="0"
                        className="loc-input-num"
                        value={form.qtd_imoveis_comerciais ?? ''}
                        onChange={e => setNum('qtd_imoveis_comerciais', e.target.value)}
                    />
                </label>
            </div>
            <div className="loc-field-grid-2">
                <label className="loc-field">
                    <span className="loc-label">Outros Tipos de Imóveis</span>
                    <input
                        type="number"
                        min="0"
                        className="loc-input-num"
                        value={form.qtd_outros_imoveis ?? ''}
                        onChange={e => setNum('qtd_outros_imoveis', e.target.value)}
                    />
                </label>
                <label className="loc-field">
                    <span className="loc-label">Habitantes</span>
                    <input
                        type="number"
                        min="0"
                        className="loc-input-num"
                        value={form.qtd_habitantes ?? ''}
                        onChange={e => setNum('qtd_habitantes', e.target.value)}
                    />
                </label>
            </div>
            <div className="loc-field-grid-2">
                <label className="loc-field">
                    <span className="loc-label">Quarteirões</span>
                    <input
                        type="number"
                        min="0"
                        className="loc-input-num"
                        value={form.qtd_quarteiroes ?? ''}
                        onChange={e => setNum('qtd_quarteiroes', e.target.value)}
                    />
                </label>
                <label className="loc-field">
                    <span className="loc-label">Pontos Estratégicos</span>
                    <input
                        type="number"
                        min="0"
                        className="loc-input-num"
                        value={form.qtd_pontos_estrategicos ?? ''}
                        onChange={e => setNum('qtd_pontos_estrategicos', e.target.value)}
                    />
                </label>
            </div>
            <div className="loc-field-grid-2">
                <label className="loc-field">
                    <span className="loc-label">Armadilhas Instaladas</span>
                    <input
                        type="number"
                        min="0"
                        className="loc-input-num"
                        value={form.qtd_armadilhas_instaladas ?? ''}
                        onChange={e => setNum('qtd_armadilhas_instaladas', e.target.value)}
                    />
                </label>
                <label className="loc-field">
                    <span className="loc-label">Terrenos Baldios</span>
                    <input
                        type="number"
                        min="0"
                        className="loc-input-num"
                        value={form.qtd_terrenos_baldios ?? ''}
                        onChange={e => setNum('qtd_terrenos_baldios', e.target.value)}
                    />
                </label>
            </div>

            <SectionDivider label="Infra-estrutura existente na localidade (S-Sim · N-Não · I-Ignorado)" />
            <div className="loc-sni-grid">
                <SNIInput field="infra_energia_eletrica" label="Energia elétrica" value={form.infra_energia_eletrica} onChange={set} />
                <SNIInput field="infra_agua_encanada" label="Água encanada" value={form.infra_agua_encanada} onChange={set} />
                <SNIInput field="infra_tratamento_esgoto" label="Tratamento de esgoto" value={form.infra_tratamento_esgoto} onChange={set} />
                <SNIInput field="infra_lavanderia_coletiva" label="Lavanderia coletiva" value={form.infra_lavanderia_coletiva} onChange={set} />
                <SNIInput field="infra_casas_privada" label="Casas com privada" value={form.infra_casas_privada} onChange={set} />
                <SNIInput field="infra_coleta_lixo" label="Coleta de lixo" value={form.infra_coleta_lixo} onChange={set} />
                <SNIInput field="infra_rede_telefonica" label="Rede telefônica" value={form.infra_rede_telefonica} onChange={set} />
                <SNIInput field="infra_transporte_publico" label="Transporte público" value={form.infra_transporte_publico} onChange={set} />
                <SNIInput field="infra_rua_pavimentada" label="Rua pavimentada" value={form.infra_rua_pavimentada} onChange={set} />
                <SNIInput field="infra_escola" label="Escola" value={form.infra_escola} onChange={set} />
                <SNIInput field="infra_posto_saude" label="Posto de saúde" value={form.infra_posto_saude} onChange={set} />
                <SNIInput field="infra_acesso_permanente" label="Acesso permanente" value={form.infra_acesso_permanente} onChange={set} />
                <SNIInput field="infra_pacs_psf" label="PACS / PSF" value={form.infra_pacs_psf} onChange={set} />
            </div>

            <SectionDivider label="Ocorrência de animais (S-Sim · N-Não · I-Ignorado)" />
            <div className="loc-sni-grid">
                <SNIInput field="ocor_animais_cachorros" label="Cachorros" value={form.ocor_animais_cachorros} onChange={set} />
                <SNIInput field="ocor_animais_gatos" label="Gatos" value={form.ocor_animais_gatos} onChange={set} />
                <SNIInput field="ocor_animais_roedores" label="Roedores" value={form.ocor_animais_roedores} onChange={set} />
            </div>

            <SectionDivider label="Ocorrência ou risco de ocorrência (S-Sim · N-Não · I-Ignorado)" />
            <div className="loc-sni-grid">
                <SNIInput field="ocor_malaria" label="Malária" value={form.ocor_malaria} onChange={set} />
                <SNIInput field="ocor_dengue" label="Dengue" value={form.ocor_dengue} onChange={set} />
                <SNIInput field="ocor_esquistossomose" label="Esquistossomose" value={form.ocor_esquistossomose} onChange={set} />
                <SNIInput field="ocor_leishmaniose" label="Leishmaniose" value={form.ocor_leishmaniose} onChange={set} />
                <SNIInput field="ocor_febre_maculosa" label="Febre Maculosa" value={form.ocor_febre_maculosa} onChange={set} />
                <SNIInput field="ocor_peste" label="Peste" value={form.ocor_peste} onChange={set} />
                <SNIInput field="ocor_doenca_chagas" label="Doença de Chagas" value={form.ocor_doenca_chagas} onChange={set} />
                <SNIInput field="ocor_febre_amarela" label="Febre Amarela" value={form.ocor_febre_amarela} onChange={set} />
            </div>

            <SectionDivider label="Dados Geográficos — WGS84" />
            <div className="loc-field-grid-2">
                <label className="loc-field">
                    <span className="loc-label">Longitude</span>
                    <input
                        type="number"
                        step="any"
                        className="loc-input-num"
                        value={form.longitude ?? ''}
                        onChange={e => set('longitude', e.target.value)}
                        placeholder="Ex: -46.633309"
                    />
                </label>
                <label className="loc-field">
                    <span className="loc-label">Latitude</span>
                    <input
                        type="number"
                        step="any"
                        className="loc-input-num"
                        value={form.latitude ?? ''}
                        onChange={e => set('latitude', e.target.value)}
                        placeholder="Ex: -23.550520"
                    />
                </label>
            </div>
            <div className="loc-field-grid-2">
                <label className="loc-field">
                    <span className="loc-label">Altitude (m)</span>
                    <input
                        type="number"
                        step="any"
                        className="loc-input-num"
                        value={form.altitude_m ?? ''}
                        onChange={e => set('altitude_m', e.target.value)}
                        placeholder="Ex: 760"
                    />
                </label>
                <label className="loc-field">
                    <span className="loc-label">Distância do Centro (Km)</span>
                    <input
                        type="number"
                        step="any"
                        className="loc-input-num"
                        value={form.distancia_centro_km ?? ''}
                        onChange={e => set('distancia_centro_km', e.target.value)}
                        placeholder="Ex: 12.5"
                    />
                </label>
            </div>

            <SectionDivider label="Visto do Supervisor" />
            <div className="loc-field-grid-2">
                <label className="loc-field">
                    <span className="loc-label">Nome do Supervisor</span>
                    <input
                        className="loc-input"
                        value={form.visto_supervisor || ''}
                        onChange={e => set('visto_supervisor', e.target.value)}
                        placeholder="Nome do supervisor"
                    />
                </label>
                <label className="loc-field">
                    <span className="loc-label">Data do Visto</span>
                    <input
                        type="date"
                        className="loc-input-date"
                        value={form.data_visto || ''}
                        onChange={e => set('data_visto', e.target.value)}
                    />
                </label>
            </div>
        </div>
    );
});

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const LocalidadesTab = ({ municipioId }) => {
    const [localidades, setLocalidades] = useState([]);
    const [, setListLoading] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [busca, setBusca] = useState('');
    const [editandoId, setEditandoId] = useState(null);

    const [modoSelecao, setModoSelecao] = useState(false);
    const [selecionados, setSelecionados] = useState(new Set());
    const [gerandoZip, setGerandoZip] = useState(false);

    const [form, setFormState] = useState({ ...FORM_VAZIO, tipo_registro: 'inclusao' });
    const setFieldValue = useCallback((field, value) => setFormState(prev => ({ ...prev, [field]: value })), []);
    const formRef = useRef(null);

    const resolvedMunicipioId = municipioId || localStorage.getItem('user_municipio_id');

    const fetchMunicipioInfo = useCallback(async () => {
        if (!resolvedMunicipioId) return;
        const { data } = await supabase
            .from('municipios')
            .select('nome, codigo_ibge, estado')
            .eq('id', resolvedMunicipioId)
            .single();
        if (data) {
            const valorIbge = data.codigo_ibge
                ? `${data.codigo_ibge} - ${data.nome}`
                : data.nome;
            setFormState(prev => ({ ...prev, codigo_municipio_ibge: valorIbge }));
        }
    }, [resolvedMunicipioId]);

    useEffect(() => { fetchMunicipioInfo(); }, [fetchMunicipioInfo]);

    const fetchLocalidades = useCallback(async () => {
        if (!resolvedMunicipioId) return;
        setListLoading(true);
        try {
            const { data, error } = await supabase
                .from('localidades')
                .select('*')
                .eq('municipio_id', resolvedMunicipioId)
                .order('nome', { ascending: true });
            if (error) throw error;
            setLocalidades(data || []);
        } catch { } finally {
            setListLoading(false);
        }
    }, [resolvedMunicipioId, setListLoading]);

    useEffect(() => { fetchLocalidades(); }, [fetchLocalidades]);

    const resetForm = useCallback(() => {
        setEditandoId(null);
        setFormState({ ...FORM_VAZIO, tipo_registro: 'inclusao' });
        fetchMunicipioInfo();
    }, [fetchMunicipioInfo]);

    const handleSalvar = async (e) => {
        if (e) e.preventDefault();
        if (!form.nome) { alert('O nome da localidade é obrigatório.'); return; }
        setSalvando(true);
        try {
            const payload = { ...buildPayload(form), municipio_id: resolvedMunicipioId };
            if (editandoId) {
                const { error } = await supabase.from('localidades').update(payload).eq('id', editandoId);
                if (error) throw error;
                alert('Localidade atualizada com sucesso!');
            } else {
                const { error } = await supabase.from('localidades').insert(payload);
                if (error) throw error;
                alert('Localidade cadastrada com sucesso!');
            }
            resetForm();
            fetchLocalidades();
        } catch (err) {
            alert('Erro ao salvar localidade.');
        } finally {
            setSalvando(false);
        }
    };

    const handleCarregarParaEdicao = useCallback((loc) => {
        setEditandoId(loc.id);
        setFormState({ ...FORM_VAZIO, ...loc, tipo_registro: 'alteracao' });
        fetchMunicipioInfo();
        setTimeout(() => {
            formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 80);
    }, [fetchMunicipioInfo]);

    const handleExcluir = useCallback(async (id) => {
        if (!window.confirm('Excluir esta localidade?')) return;
        try {
            const { error } = await supabase.from('localidades').delete().eq('id', id);
            if (error) throw error;
            if (editandoId === id) resetForm();
            fetchLocalidades();
        } catch { alert('Erro ao excluir.'); }
    }, [editandoId, resetForm, fetchLocalidades]);

    const resumosFiltrados = useMemo(() => {
        if (!busca.trim()) return localidades;
        const q = busca.toLowerCase();
        return localidades.filter(l =>
            (l.nome || '').toLowerCase().includes(q) ||
            (l.codigo || '').toLowerCase().includes(q) ||
            (l.categoria || '').toLowerCase().includes(q)
        );
    }, [localidades, busca]);

    const handleBaixarZip = async () => {
        if (selecionados.size === 0) return;
        setGerandoZip(true);
        const zip = new JSZip();
        try {
            const selecionadasData = localidades.filter(l => selecionados.has(l.id));
            for (let i = 0; i < selecionadasData.length; i++) {
                const loc = selecionadasData[i];
                const doc = <FormularioLocalidadePDFDocument form={loc} />;
                const blob = await pdf(doc).toBlob();
                zip.file(`RG03_${loc.nome || i}.pdf`.replace(/[\\/?%*:|"<>]/g, '-'), blob);
            }
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            saveAs(zipBlob, `Localidades_RG03.zip`);
        } catch { alert("Erro ao gerar ZIP."); } finally { setGerandoZip(false); }
    };

    const toggleSelecionado = useCallback((id) => {
        setSelecionados(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const toggleTodos = useCallback(() => {
        if (selecionados.size === resumosFiltrados.length) setSelecionados(new Set());
        else setSelecionados(new Set(resumosFiltrados.map(r => r.id)));
    }, [selecionados.size, resumosFiltrados]);

    const statusLabel = (s) => s === '2' ? 'Extinta' : 'Ativa';
    const classifLabel = (c) => c || '—';

    return (
        <div ref={formRef} className="loc-container">
            <div className="loc-form">
                <div className="loc-form-header">
                    <span className="loc-form-header-title">
                        {editandoId ? 'Editando Localidade' : 'Novo Registro de Localidade'}
                    </span>
                    {editandoId && (
                        <div className="loc-edit-badge">
                            ✏️ Modo edição
                            <button className="loc-edit-cancel" onClick={resetForm}>Cancelar</button>
                        </div>
                    )}
                </div>
                <div className="loc-form-body">
                    <FormFields form={form} set={setFieldValue} formPrefix="loc" />
                    <div className="loc-action-bar">
                        <button type="button" className="loc-btn-save" disabled={salvando} onClick={handleSalvar}>
                            {salvando ? 'Salvando...' : editandoId ? 'Salvar Alterações' : 'Cadastrar Localidade'}
                        </button>
                        {editandoId && <BotaoDownloadLocalidadePDF form={form} />}
                    </div>
                </div>
            </div>

            <div className="loc-list-card">
                <div className="loc-table-toolbar">
                    <h3 className="loc-table-title">Localidades Cadastradas</h3>
                    <div className="loc-toolbar-actions">
                        <button className="btn-download-trigger" onClick={() => { setModoSelecao(!modoSelecao); setSelecionados(new Set()); }}>
                            {modoSelecao ? 'Cancelar' : 'Baixar PDFs'}
                        </button>
                        {modoSelecao && selecionados.size > 0 && (
                            <button className="btn-execute-download" onClick={handleBaixarZip} disabled={gerandoZip}>
                                {gerandoZip ? 'Gerando...' : `Baixar ${selecionados.size} itens`}
                            </button>
                        )}
                        <div className="loc-search-wrapper">
                            <input className="loc-search-input" placeholder="Buscar..." value={busca} onChange={e => setBusca(e.target.value)} />
                        </div>
                    </div>
                </div>
                <div className="loc-table-wrapper">
                    <table className="loc-table">
                        <thead>
                            <tr>
                                {modoSelecao && <th style={{ width: 40 }}><input type="checkbox" checked={selecionados.size === resumosFiltrados.length} onChange={toggleTodos} /></th>}
                                <th>Código</th><th>Nome</th><th>Categoria</th><th>Status</th><th>Classificação</th><th>Ação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {resumosFiltrados.map(loc => (
                                <tr key={loc.id} onClick={() => modoSelecao && toggleSelecionado(loc.id)}>
                                    {modoSelecao && <td><input type="checkbox" checked={selecionados.has(loc.id)} onChange={() => {}} /></td>}
                                    <td>{loc.codigo || '—'}</td><td>{loc.nome}</td><td>{loc.categoria || '—'}</td>
                                    <td>{statusLabel(loc.status_localidade)}</td><td>{classifLabel(loc.classificacao)}</td>
                                    <td>
                                        <div className="loc-table-actions">
                                            <button className="loc-btn-edit" onClick={(e) => { e.stopPropagation(); handleCarregarParaEdicao(loc); }}>Visualizar / Editar</button>
                                            <button className="loc-btn-delete" onClick={(e) => { e.stopPropagation(); handleExcluir(loc.id); }}>🗑️</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default LocalidadesTab;
